import React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import apiHelper from "../../apiHelper";

export default function useSkuSchemeActions() {

  // UI / meta state
  const [activeTab, setActiveTab] = useState("Generation");
  const breadcrumbs = ["Settings", "SKU Code Scheme"];
  const [dropdownOpenFor, setDropdownOpenFor] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [cancelEditModalOpen, setCancelEditModalOpen] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [alertInfo, setAlertInfo] = useState(null);
  const [filterText, setFilterText] = useState("");
  const [liveExample, setLiveExample] = useState({});
  const stripBraces = (ph) => ph.replace(/^\{|\}$/g, "");
  const editRefs = useRef({});
  const [editingIds, setEditingIds] = useState(new Set());
  const [cancelTargetId, setCancelTargetId] = useState(null);
  const [pageData, setPageData] = useState([]);
  const [editValue, setEditValue] = useState({});
  const [filter, setFilter] = useState("");
  const inputRefs = useRef({});
  const [alert, setAlert] = useState(null);
  const [showItemAlert, setShowItemAlert] = useState(false);




  // pagination helpers
  const itemsPerPage = 10;

  // caret + errors
  const [cursorPos, setCursorPos] = useState({});
  const [errors, setErrors] = useState({ generationRule: "" });

  // placeholder options & example values
  const placeholderOptions = [
    { label: "{BrandPrefix}", color: "#2C7BE5" },
    { label: "{CategoryPrefix}", color: "#FF00BF" },
    { label: "{SPUPrefix}", color: "#00FF37" },
    { label: "{SKUAbbr}", color: "#FF8000" },
  ];
  const exampleColorMap = placeholderOptions.reduce((acc, opt) => {
    acc[opt.label] = opt.color;
    return acc;
  }, {});
  const getExampleValueMap = (productType) => {
    const v = exampleValues[productType];
    if (!v) return {};

    return {
      "{BrandPrefix}": v.BrandPrefix,
      "{CategoryPrefix}": v.CategoryPrefix,
      "{SPUPrefix}": v.SPUPrefix,
      "{SKUAbbr}": v.SKUAbbr,
    };
  };

  const renderColoredExample = (scheme = "", productType = "") => {
    const valueMap = getExampleValueMap(productType) || {};

    const parts = [];
    let remaining = scheme;

    while (remaining.length > 0) {
      const match = remaining.match(/\{.*?\}/);

      if (!match) {
        parts.push(<span>{remaining}</span>);
        break;
      }

      const start = match.index;
      const end = start + match[0].length;
      const placeholder = match[0];

      // Add text before placeholder
      if (start > 0) {
        parts.push(<span>{remaining.slice(0, start)}</span>);
      }

      const value = valueMap[placeholder];

      // ✔ If placeholder value is "blank" → remove placeholder completely
      if (value && value.toLowerCase() === "blank") {
        // DO NOT add anything to parts (skip)
        remaining = remaining.slice(end);
        continue;
      }

      // ✔ Known placeholder with real value → replace + color
      if (value) {
        const color = exampleColorMap[placeholder] || "#000";

        parts.push(
          <span style={{ color }}>{value}</span>
        );
      }
      else {
        // ✔ Unknown placeholder → show as-is
        parts.push(<span>{placeholder}</span>);
      }

      remaining = remaining.slice(end);
    }

    return parts;
  };


  useEffect(() => {
    async function fetchSkuRules() {
      try {
        const res = await apiHelper("settings/sku-code-generation-rules/list/", "GET");

        if (res.ok) {
          const result = await res.json();
          console.log("Fetched SKU rules:", result);
          setPageData(result);
        } else {
          console.error("API returned error:", res.status);
        }
      } catch (err) {
        console.error("Failed to fetch SKU rules:", err);
      }
    }

    fetchSkuRules();
  }, []);

  const exampleValues = {
    Device: {
      BrandPrefix: "APL",
      CategoryPrefix: "IP",
      SPUPrefix: "16E",
      SKUAbbr: "BLK8G256G",
    },
    "Spare Part": {
      BrandPrefix: "APL",
      CategoryPrefix: "CAM",
      SPUPrefix: "IP5S",
      SKUAbbr: "BACK",
    },
    Accessory: {
      BrandPrefix: "APL",
      CategoryPrefix: "GLS",
      SPUPrefix: "IP16",
      SKUAbbr: "TP",
    },
    Others: {
      BrandPrefix: "MM",
      CategoryPrefix: "TOPUP",
      SPUPrefix: "NEW",
      SKUAbbr: "blank",
    },
  };

  const generateExample = useCallback((scheme = "", productType = "") => {
    const val = exampleValues[productType] || {};

    return scheme.replace(/\{[^}]+\}/g, (match) => {
      const key = match.replace(/[{}]/g, "");
      const value = val[key];

      // ❗ If value is "blank" or empty → REMOVE completely
      if (!value || value.toLowerCase() === "blank") {
        return "";
      }

      // normal replacement
      return value;
    });
  }, []);

  // input schemes (for editing UI)
  const [inputSchemes, setInputSchemes] = useState([
    { id: 1, productType: "", options: [], newOptionInput: "", example: "" },
  ]);



  // other small data table (variations)
  const [data, setData] = useState([
    { id: 1, skuVariation: "Color", variationOption: "Pink", caseSensitive: "YES", abbreviation: "PK", remarks: "NA" },
    { id: 2, skuVariation: "Color", variationOption: "Blue", caseSensitive: "YES", abbreviation: "BLU", remarks: "NA" },
    { id: 3, skuVariation: "Color", variationOption: "Yellow", caseSensitive: "YES", abbreviation: "YELW", remarks: "NA" },
    { id: 4, skuVariation: "Color", variationOption: "Desert", caseSensitive: "YES", abbreviation: "DST", remarks: "NA" },
    { id: 5, skuVariation: "Color", variationOption: "White", caseSensitive: "YES", abbreviation: "WHT", remarks: "NA" },
    { id: 6, skuVariation: "Color", variationOption: "Black", caseSensitive: "YES", abbreviation: "BLK", remarks: "NA" },
  ]);

  // editing values map: id => scheme string
  const [editRow, setEditRow] = useState({});

  // pagination helpers derived from pageData + currentPage
  const sortedFiltered = pageData.filter((r) =>
    !filterText ? true : (r.product_type || "").toLowerCase().includes(filterText.toLowerCase())
  );
  const startIndex = (currentPage - 1) * itemsPerPage + 1;
  const endIndex = Math.min(currentPage * itemsPerPage, sortedFiltered.length);
  const paginatedData = sortedFiltered.slice(startIndex - 1, endIndex);

  // caret helper
  const getCaretCharacterOffsetWithin = useCallback((element) => {
    if (!element) return 0;
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return 0;
    const range = sel.getRangeAt(0);
    const preCaretRange = range.cloneRange();
    preCaretRange.selectNodeContents(element);
    preCaretRange.setEnd(range.endContainer, range.endOffset);
    return preCaretRange.toString().length;
  }, []);

  // styles helpers
  const tagStyle = useCallback((placeholder) => {
    const matched = placeholderOptions.find((p) => p.label === placeholder);
    return {
      color: matched?.color || "#000",
      borderRadius: "4px",
      fontSize: "13px",
      lineHeight: "normal",
    };
  }, [placeholderOptions]);

  const examplePartStyle = useCallback((part) => {
    const matched = placeholderOptions.find((p) => p.label === part);
    return matched
      ? { color: matched.color, borderRadius: "4px", fontWeight: 400, fontSize: '12px' }
      : { color: "black" };
  }, [placeholderOptions]);

  // render scheme inline with non-editable placeholder spans (returns JSX)
  const renderInlineScheme = useCallback((scheme = "") => {
    const parts = scheme.split(/({.*?})/g);

    return parts.map((part, i) => {
      if (/{.*?}/.test(part)) {
        return (
          <React.Fragment key={i}>
            <span
              data-ph={part}
              contentEditable={false}
              className="phTag"
              style={tagStyle(part)}
            >
              {part}
            </span>
            {" "}
          </React.Fragment>
        );
      }

      // normal text: DO NOT wrap in span
      return <React.Fragment key={i}>{part}</React.Fragment>;
    });
  }, [tagStyle]);


  // get placeholders from a scheme or id
  const getSelectedPlaceholders = useCallback((schemeOrId) => {
    if (typeof schemeOrId === "number") {
      const row = pageData.find((r) => r.sku_code_generation_id === schemeOrId);
      return Array.from(new Set(((row?.scheme) || "").match(/\{.*?\}/g) || []));
    }
    const scheme = schemeOrId || "";
    return Array.from(new Set((scheme.match(/\{.*?\}/g) || [])));
  }, [pageData]);



  // handle changes inside contentEditable container -> rebuild scheme
  const handleSkuChange = useCallback((e, id) => {
    const container = editRefs.current[id];
    if (!container) return;
    let newScheme = "";
    container.childNodes.forEach((node) => {
      if (node.nodeType === Node.ELEMENT_NODE && node.getAttribute?.("data-ph")) {
        newScheme += node.getAttribute("data-ph");
      } else {
        newScheme += node.textContent || "";
      }
    });

    setEditRow(prev => ({ ...prev, [id]: newScheme }));

    const row = pageData.find(r => r.sku_code_generation_id === id) || {};
    setLiveExample(prev => ({ ...prev, [id]: generateExample(newScheme, row.product_type) }));

    setErrors(prev => ({ ...prev, generationRule: newScheme.trim() ? "" : "Generation Rule is required" }));
    setIsDirty(true);
  }, [pageData, generateExample]);

  // open placeholder dropdown and record caret pos when user types '{'
  const handleKeyDown = useCallback((e, id) => {
    setDropdownOpenFor(id);
    const container = editRefs.current[id];
    setCursorPos(prev => ({ ...prev, [id]: getCaretCharacterOffsetWithin(container) }));
  }, [getCaretCharacterOffsetWithin]);

  // support Backspace to remove preceding placeholder span
  const handleBackspaceRemove = (e, id) => {
    if (e.key !== "Backspace") return;

    const sel = window.getSelection();
    if (!sel.rangeCount) return;

    const node = sel.anchorNode;

    if (node.nodeType === 3 && sel.anchorOffset === 0) {
      const prev = node.previousSibling;

      if (prev?.getAttribute?.("data-ph")) {
        prev.remove();     // DIRECT DOM DELETE
        e.preventDefault();
        handleSkuChange(null, id);  // update scheme
      }
    }
  };

  const removePlaceholder = useCallback((placeholder, id) => {
    const current = editRow[id] ?? pageData.find(r => r.sku_code_generation_id === id)?.scheme ?? "";
    const updated = current.replace(placeholder, "");
    setEditRow(prev => ({ ...prev, [id]: updated }));
    const row = pageData.find(r => r.sku_code_generation_id === id) || {};
    const example = generateExample(updated, row.product_type);
    setLiveExample(prev => ({ ...prev, [id]: example }));
    setIsDirty(true);
  }, [editRow, pageData, generateExample]);

  // start editing a row
  const startEdit = useCallback((row) => {
    const id = row.sku_code_generation_id;

    setEditingIds(prev => {
      const next = new Set(prev);
      next.add(id);      // allow multiple open edits
      return next;
    });

    setIsDirty(true);

    setEditRow((prev) => ({
      ...prev,
      [id]: row.scheme || "",
    }));

    setInputSchemes((prev) => [
      {
        id: id,
        productType: row.product_type || "",
        options: (row.scheme || "").match(/\{.*?\}/g) || [],
        newOptionInput: "",
        example: row.example || "",
      },
      ...prev.filter((p) => p.id !== id),
    ]);

    setEditingIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);

  const handleCancelEditConfirm = useCallback(() => {
    setAlertInfo({ type: "success", recordName: "Generation Rule", action: "discard" });

    setEditingIds(prev => {
      const next = new Set(prev);
      next.delete(cancelTargetId);
      return next;
    });

    // ⭐ FIX: Restore original DB value
    setEditValue(prev => {
      const next = { ...prev };
      delete next[cancelTargetId];
      return next;
    });

    // Fix errors
    setErrors(prev => {
      const next = { ...prev };
      delete next[cancelTargetId];
      return next;
    });

    setCancelEditModalOpen(false);
    setCancelTargetId(null);
    setIsDirty(false);
  }, [cancelTargetId]);

  const handleCancelEditClose = useCallback(() => setCancelEditModalOpen(false), []);

  // validate a rule string
  const validateGenerationRule = useCallback((value) => {
    if (!value || !value.trim()) {
      setErrors(prev => ({ ...prev, generationRule: "Generation Rule is required" }));
      return false;
    }
    setErrors(prev => ({ ...prev, generationRule: "" }));
    return true;
  }, []);

  const saveEdit = async (id) => {
    const row = pageData.find(r => r.sku_code_generation_id === id);

    // The true visible value in the UI
    const visibleValue =
      (editValue[id] !== undefined ? editValue[id] : row.scheme) || "";

    // ❗ VALIDATE correctly
    if (!visibleValue.trim()) {
      setErrors(prev => ({
        ...prev,
        [id]: "Generation Rule is required"
      }));

      setAlertInfo({
        type: "error",
        recordName: "Generation Rule",
        action: "update"
      });

      return; // stop save
    }

    // POST the visible value
    const res = await apiHelper(
      `settings/sku-code-generation-rules/update/${id}/`,
      "POST",
      { scheme: visibleValue }
    );

    if (res.ok) {
      setPageData(prev =>
        prev.map(r =>
          r.sku_code_generation_id === id
            ? { ...r, scheme: visibleValue }
            : r
        )
      );
    }

    // clear edit state
    setEditingIds(prev => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });

    setErrors(prev => {
      const next = { ...prev };
      delete next[id];
      return next;
    });

    setAlertInfo({
      type: "success",
      recordName: "Generation Rule",
      action: "update"
    });
  };

  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [isDirty]);

  const parsePlaceholders = useCallback((scheme = "") => {
    return Array.from(new Set((scheme.match(/\{.*?\}/g) || [])));
  }, []);

  const getPlaceholderOptions = () => placeholderOptions;

  const handleKey = (e, id) => {
    e.persist();
    const input = inputRefs.current[id];
    const value = editValue[id] ?? "";

    // User typed "{"
    if (e.key === "{") {
      // allow "{" to be added to input
      setTimeout(() => {
        openDropdown(id);

        const cursor = input.selectionStart;
        const value = input.value;
        const lastBrace = value.lastIndexOf("{", cursor);

        // Compute search text INCLUDING "{"
        const searchText = value.substring(lastBrace, cursor);

        setFilter(searchText);  // <-- NOW filter has "{"
      }, 0);

      return;
    }


    console.log("HANDLE KEY:", e.key, dropdownOpenFor, id);
    console.log("HANDLE KEY:", filter);

    // If dropdown not open → normal typing
    if (dropdownOpenFor !== id) return;

    if (/^[a-zA-Z]$/.test(e.key)) {
      setTimeout(() => {
        const cursor = input.selectionStart;
        const v = input.value;
        const lastBrace = v.lastIndexOf("{", cursor);

        if (lastBrace !== -1) {
          setFilter(v.substring(lastBrace, cursor)); // "{b", "{br", etc.
        }
      }, 0);
      return;
    }



    if (e.key === "Backspace") {
      setTimeout(() => {
        const cursor = input.selectionStart;
        const v = input.value;
        const lastBrace = v.lastIndexOf("{", cursor);

        if (lastBrace !== -1) {
          setFilter(v.substring(lastBrace, cursor));
        } else {
          setFilter("");
        }
      }, 0);
      return;
    }


    if (e.key === "Escape") {
      setDropdownOpenFor(null);
      setFilter("");
      return;
    }
  };


  const insertPlaceholder = (placeholder, id) => {
    const input = inputRefs.current[id];
    const value = editValue[id] ?? "";
    const cursorPos = input.selectionStart;

    let newVal;
    let removeLength = filter.length;

    // 🔥 FIX: If user only typed "{", filter is empty → but we MUST remove it
    if (removeLength === 0 && value[cursorPos - 1] === "{") {
      removeLength = 1;   // remove the single typed "{"
    }

    newVal =
      value.substring(0, cursorPos - removeLength) +
      placeholder +
      value.substring(cursorPos);

    setEditValue(prev => ({
      ...prev,
      [id]: newVal,
    }));

    // Reset search text but KEEP dropdown open
    setFilter("");
    setDropdownOpenFor(null);

    // Move cursor after placeholder
    setTimeout(() => {
      input.focus();
      const pos = cursorPos - removeLength + placeholder.length;
      input.setSelectionRange(pos, pos);
    }, 0);
  };

  const openDropdown = (id) => {
    setDropdownOpenFor(id);
    const inputElement = inputRefs.current[id];

    setTimeout(() => {
      if (inputElement) {
        const rect = inputElement.getBoundingClientRect();

        setDropdownPosition(prev => ({
          ...prev,
          [id]: {
            top: rect.bottom + window.scrollY,
            left: rect.left + window.scrollX,
            // width: rect.width
          }
        }));
      }
    }, 0);
  };
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!dropdownOpenFor) return;

      const input = inputRefs.current[dropdownOpenFor];
      const dropdown = document.getElementById(`dropdown-${dropdownOpenFor}`);

      // If click is inside input → ignore
      if (input && input.contains(event.target)) return;

      // If click is inside dropdown → ignore
      if (dropdown && dropdown.contains(event.target)) return;

      // Otherwise → close dropdown
      setDropdownOpenFor(null);
      setFilter("");
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [dropdownOpenFor]);


  // Add a new state in your component to hold the coordinates
  const [dropdownPosition, setDropdownPosition] = useState({});

  // Add a new state in your component
  const [upwardDropdownId, setUpwardDropdownId] = useState(null);
  useEffect(() => {
    Object.entries(editValue).forEach(([id, value]) => {
      const row = pageData.find(r => r.sku_code_generation_id == id);
      if (!row) return;

      const ex = generateExample(value, row.product_type);

      setLiveExample(prev => ({
        ...prev,
        [id]: ex
      }));
    });
  }, [editValue, pageData, generateExample]);


  return {
    // state & setters
    activeTab,
    setActiveTab,
    breadcrumbs,
    dropdownOpenFor,
    setDropdownOpenFor,
    currentPage,
    setCurrentPage,
    cancelEditModalOpen,
    setCancelEditModalOpen,
    isDirty,
    setIsDirty,
    alertInfo,
    setAlertInfo,
    filterText,
    setFilterText,
    liveExample,
    setLiveExample,
    editRefs,
    editingIds,
    setEditingIds,
    cancelTargetId,
    setCancelTargetId,
    itemsPerPage,
    sortedFiltered,
    paginatedData,
    cursorPos,
    setCursorPos,
    errors,
    setErrors,

    // data
    pageData,
    setPageData,
    inputSchemes,
    setInputSchemes,
    data,
    setData,
    editRow,
    setEditRow,

    // helpers & renderers
    getPlaceholderOptions,
    placeholderOptions,
    parsePlaceholders,
    getSelectedPlaceholders,
    generateExample,
    renderInlineScheme,
    tagStyle,
    examplePartStyle,

    // actions
    startEdit,
    handleCancelEditConfirm,
    handleCancelEditClose,
    saveEdit,
    insertPlaceholder,
    handleSkuChange,
    handleKeyDown,
    handleBackspaceRemove,
    removePlaceholder,
    validateGenerationRule,
    getCaretCharacterOffsetWithin,
    handleKey,
    editValue,
    setEditValue,
    filter,
    setFilter,
    inputRefs,
    openDropdown,
    upwardDropdownId,
    setUpwardDropdownId,
    dropdownPosition,
    setDropdownPosition,
    renderColoredExample,
    alert,
    setAlert,
    showItemAlert,
    setShowItemAlert,
  };
}
